import { motion } from "motion/react";
import {
  BarChart3,
  Globe,
  Link,
  Shield,
  Zap,
  QrCode,
  Lock,
  Smartphone,
  Tag,
} from "lucide-react";

const features = [
  {
    icon: Link,
    title: "CUSTOM_ALIASES",
    description: "Create memorable, branded links with your own custom codes.",
    ascii: `┌──┐──┌──┐
│ L│──│NK│
└──┘──└──┘`,
  },
  {
    icon: BarChart3,
    title: "DEEP_ANALYTICS",
    description: "Track clicks, devices, browsers, locations, and referrers.",
    ascii: `  │▓▓▓
  │▓▓▓▓▓
  │▓▓▓▓▓▓▓
  └───────`,
  },
  {
    icon: QrCode,
    title: "QR_GENERATION",
    description: "Generate QR codes for any link instantly.",
    ascii: `█▀▀█ ▀█▀
█▄▄█ ▄█▄
▀  ▀ ▀▀▀`,
  },
  {
    icon: Lock,
    title: "PASSWORD_PROTECT",
    description: "Secure sensitive links with password protection.",
    ascii: `  ┌───┐
  │ ▓ │
┌─┴───┴─┐
│░░░░░░░│
└───────┘`,
  },
  {
    icon: Smartphone,
    title: "DEVICE_TARGETING",
    description: "Redirect users based on their device (iOS/Android).",
    ascii: `┌─────┐
│ ═══ │
│     │
│  ○  │
└─────┘`,
  },
  {
    icon: Tag,
    title: "UTM_BUILDER",
    description: "Add UTM parameters for campaign tracking.",
    ascii: `?utm_source
&utm_medium
&utm_campaign`,
  },
  {
    icon: Globe,
    title: "CUSTOM_OG_TAGS",
    description: "Customize how your links appear when shared.",
    ascii: `┌─────────┐
│ TITLE   │
│ ─────── │
│ desc... │
└─────────┘`,
  },
  {
    icon: Zap,
    title: "INSTANT_REDIRECT",
    description: "Lightning-fast redirects with zero delay.",
    ascii: `    ╱╲
   ╱  ╲
  ╱ ⚡ ╲
 ╱──────╲`,
  },
  {
    icon: Shield,
    title: "SECURE_INFRA",
    description: "Enterprise-grade security and 100% uptime.",
    ascii: `   ╱▔▔╲
  ╱ ✓✓ ╲
 ╱  ✓✓  ╲
╱────────╲`,
  },
];

export function LFeaturesSection() {
  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
                        linear-gradient(#9B87F5 1px, transparent 1px),
                        linear-gradient(90deg, #9B87F5 1px, transparent 1px)
                    `,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <pre className="font-mono text-[#9B87F5] text-xs mb-4 inline-block">
            {`╔════════════════════════════════════════════════════════════╗
║                    FEATURE_MANIFEST                        ║
╚════════════════════════════════════════════════════════════╝`}
          </pre>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-mono mb-4">
            CAPABILITIES.<span className="text-[#9B87F5]">ANALYZE</span>()
          </h2>
          <p className="text-lg text-[#8E9196] font-mono max-w-2xl mx-auto">
            // Every tool you need to track, manage, and optimize your links
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative"
            >
              <div className="p-6 rounded-lg bg-[#1A1F2C]/50 border border-[#9B87F5]/10 hover:border-[#9B87F5]/30 transition-all duration-300 h-full">
                {/* ASCII art */}
                <pre className="font-mono text-[#9B87F5]/50 text-[10px] mb-4 group-hover:text-[#9B87F5] transition-colors">
                  {feature.ascii}
                </pre>

                {/* Icon and title */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#9B87F5]/10 flex items-center justify-center group-hover:bg-[#9B87F5]/20 transition-colors">
                    <feature.icon className="w-5 h-5 text-[#9B87F5]" />
                  </div>
                  <h3 className="text-lg font-bold text-white font-mono">
                    {feature.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-[#8E9196] font-mono text-sm leading-relaxed">
                  {feature.description}
                </p>

                {/* Corner decoration */}
                <div className="absolute top-2 right-2 font-mono text-[#9B87F5]/20 text-xs">
                  [{String(index + 1).padStart(2, "0")}]
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom ASCII decoration */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <pre className="font-mono text-[#9B87F5]/30 text-xs inline-block">
            {`═══════════════════════════════════════════════════════════════
                    END_OF_FEATURE_LIST
═══════════════════════════════════════════════════════════════`}
          </pre>
        </motion.div>
      </div>
    </section>
  );
}
